import type { Rol } from '@pasos/schemas';

/**
 * El copy del sistema · los estados transversales S-01 a S-13 (ADR-016).
 *
 * Acá vive el texto que **cualquier rol** puede ver: carga, vacío, error, éxito,
 * sesión vencida, sin permiso, offline y las confirmaciones. Está separado de
 * los módulos por rol porque su tono es una decisión de producto que vale para
 * todo el producto: **el error es del sistema, nunca del usuario**.
 *
 * Regla del ADR: el copy es dato. Cada entrada es una **frase entera y legible**;
 * las variantes son entradas distintas, no un ternario armado en la pantalla.
 */
/** Lo que quedó esperando la conexión, como frases enteras. */
const LO_QUE_ESPERA = Object.freeze({
  ninguna: 'Podés seguir respondiendo tus check-ins. El chat con PIA vuelve con la conexión.',
  una: 'Tu respuesta se va a enviar cuando vuelva la conexión.',
  varias: (cuantas: number) =>
    `Tus ${cuantas} respuestas se van a enviar cuando vuelva la conexión.`,
});

export const SISTEMA = Object.freeze({
  cargando: {
    generico: 'Un momento…',
    home: 'Cargando tu información',
    lista: 'Cargando',
    sesion: 'Cargando tu sesión…',
  },

  error: {
    titulo: 'Algo no salió como esperábamos',
    detalle: 'No pudimos cargar esta parte. Tus datos están a salvo.',
    reintentar: 'Reintentar',
  },

  /**
   * S-09 · la sesión se cortó por inactividad.
   *
   * Estuvo escrita desde E2 y **no la usaba nadie**: ante un 401 la app mandaba
   * al inicio, que dice "Para entrar necesitás tu enlace" — un enlace que ya está
   * gastado (H-10 y H-24). Ahora se muestra cuando corresponde.
   *
   * **Desde E3.24 hay por dónde volver**, y es distinto según el rol: el equipo
   * vuelve a poner su contraseña, con el botón; el paciente y el cuidador ven
   * esta pantalla sólo si la renovación con su dispositivo ya falló, y lo que
   * les queda es pedir un enlace nuevo, que el equipo ahora puede generar (A04).
   */
  //
  // **Un texto por rol desde la devolución del 04/10 (D-314).** Al paciente y al
  // cuidador no se les corta la sesión a los 30 minutos (los cubre el
  // dispositivo recordado, INGRESO-1), y el equipo no "responde": cada uno lee
  // lo que le pasó de verdad. Los 30 minutos son `MINUTOS_DE_INACTIVIDAD`.
  // PROPUESTO · pendiente de validación de Fundación (E3.24)
  sesionExpirada: {
    // PROPUESTO · pendiente de validación de Fundación (devolución 04/10)
    equipo: {
      titulo: 'Tu sesión se cerró por inactividad',
      detalle:
        'Por seguridad cerramos tu sesión después de 30 minutos sin actividad. Lo que ya habías guardado no se perdió.',
    },
    // PROPUESTO · pendiente de validación de Fundación (devolución 04/10)
    paciente: {
      titulo: 'Necesitás entrar de nuevo',
      detalle:
        'Por seguridad, este teléfono ya no tiene guardado tu acceso. Lo que respondiste antes quedó registrado.',
    },
    comoVolverEquipo: 'Para seguir, volvé a entrar con tu email y tu contraseña.',
    volverAEntrar: 'Volver a entrar',
    comoVolverPaciente: 'Para volver a entrar, pedile un enlace nuevo a tu equipo médico.',
  },

  sinPermiso: {
    titulo: 'Esta sección no es parte de tu rol',
    /** Sin rol conocido: la frase entera, no un fragmento que se completa afuera. */
    detalleSinRol:
      'No tenés acceso a este contenido. Si creés que es un error, contactá al equipo de la institución.',
    detalleConRol: (rol: string) =>
      `Con tu rol (${rol}), no tenés acceso a este contenido. Si creés que es un error, contactá al equipo de la institución.`,
    volver: 'Volver',
  },

  mantenimiento: {
    titulo: 'Mantenimiento programado',
  },

  /**
   * S-08 · sin conexión, a pantalla completa.
   *
   * Es lo que ve alguien con sesión válida cuando el servidor no contesta. La
   * frase dice **sólo lo que pasa**: lo que ya respondió está guardado y sale
   * solo; lo que todavía no empezó necesita conexión, porque qué le toca hoy se
   * lo pide al servidor y esa respuesta no se guarda en el teléfono a propósito
   * (H-7). PROPUESTO, pendiente de validación de Fundación.
   */
  sinConexion: {
    titulo: 'Sin conexión',
    detalle:
      'No pudimos comunicarnos con el servidor. Lo que ya respondiste está guardado y se va a enviar solo cuando vuelva la conexión.',
    queHacer: 'Cuando tengas señal de nuevo, esta pantalla se actualiza sola.',
  },

  offline: {
    titulo: 'Sin conexión · trabajando offline',
    sinPendientes:
      'Podés seguir respondiendo tus check-ins. El chat con PIA vuelve con la conexión.',
    pendientes: (cuantas: number): string =>
      cuantas <= 0
        ? LO_QUE_ESPERA.ninguna
        : cuantas === 1
          ? LO_QUE_ESPERA.una
          : LO_QUE_ESPERA.varias(cuantas),
  },

  confirmacion: {
    cancelar: 'Cancelar',
  },

  /**
   * La versión nueva de la app (P-65): el service worker nuevo espera a que la
   * persona actualice. Sin urgencia ni alarma: no hay nada roto.
   */
  actualizacion: {
    // PROPUESTO · pendiente de validación de Fundación (E3.7)
    mensaje: 'Hay una versión nueva de la app.',
    // PROPUESTO · pendiente de validación de Fundación (E3.7)
    actualizar: 'Actualizar',
  },

  /**
   * El aviso del entorno de demostración (DEMO-1). Lo ve cualquier rol en todas
   * las pantallas cuando el build lo enciende; en producción no existe.
   */
  entornoDeDemostracion: {
    aviso: 'Entorno de demostración. Los datos son sintéticos.',
    rotulo: 'Entorno de demostración',
  },

  /**
   * El texto alternativo del logo. Es lo que la imagen **dice**, no copy
   * redactado: el logotipo se lee "Pasos+" desde el vector oficial (LOGO-1).
   * Si la imagen cambia, cambia esto, juntos.
   */
  marca: {
    logo: 'Pasos+',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    firma: 'Un programa de Fundación Trauma',
    // La misma firma partida, donde va con el logo de Fundación (las pantallas
    // de acceso): el texto, y el nombre como texto alternativo de la imagen.
    unProgramaDe: 'Un programa de',
    fundacion: 'Fundación Trauma',
  },

  /**
   * Cómo se nombra cada rol cuando hay que decírselo a la persona.
   *
   * En minúscula y en lenguaje corriente: la pantalla S-10 dice "con tu rol
   * (paciente)", no "con tu rol (PACIENTE)".
   */
  nombreDelRol: Object.freeze({
    PACIENTE: 'paciente',
    CUIDADOR: 'cuidador',
    PROFESIONAL: 'profesional',
    COORDINADOR: 'coordinador',
    ADMIN_INSTITUCION: 'administrador de la institución',
    ADMIN_FUNDACION: 'administrador de Fundación',
  }) as Readonly<Record<Rol, string>>,
});
